import { isAxiosError } from './errorUtils';
import { ErrorVariant } from '../../app/shared-components/generic-errors/model';

export interface ErrorConfig {
	variant: ErrorVariant;
	text: string;
}

/**
 * Maps an error to a GenericErrorTemplate variant and message.
 * Use with GenericErrorTemplate component to show appropriate error UI.
 *
 * @param error - The error from a failed request
 * @param resourceName - Optional resource name for contextual messages (e.g., "project", "environment")
 */
export const getErrorConfig = (
	error: unknown,
	resourceName = 'resource',
): ErrorConfig => {
	if (!isAxiosError(error)) {
		return {
			variant: 'error',
			text: 'An unexpected error occurred.',
		};
	}

	switch (error.response?.status) {
		case 400:
			return {
				variant: 'bad-request',
				text: 'The request was invalid.',
			};
		case 401:
			return {
				variant: 'unauthorized',
				text: `You need to sign in to access this ${resourceName}.`,
			};
		case 403:
			return {
				variant: 'access-denied',
				text: `You do not have permission to access this ${resourceName}.`,
			};
		case 404:
			return {
				variant: 'not-found',
				text: `The ${resourceName} you are looking for does not exist or has been deleted.`,
			};
		case 500:
			return {
				variant: 'server-error',
				text: 'Something went wrong on our end. Please try again later.',
			};
		case 503:
			return {
				variant: 'unavailable',
				text: 'The service is temporarily unavailable. Please try again later.',
			};
		default:
			return {
				variant: 'error',
				text: 'An unexpected error occurred.',
			};
	}
};
